/**
 * useBrandResearchPoll: polls for brand-research suggested objectives.
 *
 * When the wizard mounts immediately after onboarding, the worker that
 * populates `suggested_objectives` may not have finished yet. This hook polls
 * `refreshActiveWorkspace` every BRAND_RESEARCH_POLL_INTERVAL_MS for up to
 * BRAND_RESEARCH_POLL_MAX_ATTEMPTS and renders a placeholder while polling.
 *
 * Stop conditions:
 *   - `suggested_objectives` arrives non-empty (hasSuggestions)
 *   - hook unmounts
 *   - `research_status` becomes `'failed'` or `'completed'` (no suggestions)
 *   - 10 attempts elapse: placeholder disappears, captureException fires once
 *
 * No polling fires when there's nothing to research (no `website_url` and not
 * idle/in_progress).
 *
 * @module components/hooks/useBrandResearchPoll
 */

import { useState, useRef, useEffect } from 'react';
import type { Workspace } from '../../lib/org-types';
import { captureException } from '../../lib/telemetry/posthog/frontend';

/** Polling interval in milliseconds. */
const BRAND_RESEARCH_POLL_INTERVAL_MS = 3_000;

/** Maximum number of poll attempts before giving up. 10 x 3s = 30s budget. */
const BRAND_RESEARCH_POLL_MAX_ATTEMPTS = 10;

/**
 * Input props for useBrandResearchPoll.
 */
export interface UseBrandResearchPollInput {
  /** The currently active workspace, or null when none is selected. */
  activeWorkspace: Workspace | null;
  /** Async function that refetches the active workspace from the server. */
  refreshActiveWorkspace: () => Promise<Workspace | null>;
}

/**
 * Return value from useBrandResearchPoll.
 */
export interface UseBrandResearchPollResult {
  /**
   * True while we are actively polling for brand-research suggestions.
   * The wizard renders a placeholder spinner while this is true.
   */
  showResearchPlaceholder: boolean;
}

/**
 * Polls for brand-tailored objective suggestions that may arrive late.
 *
 * Mirrors the polling logic from CreateInterviewForm.tsx lines 518-592.
 * Each workspace gets an independent 30s polling budget. The budget resets
 * when the active workspace changes.
 *
 * @param input - Active workspace and refresh callback.
 * @returns Whether to show the research placeholder.
 */
export function useBrandResearchPoll(input: UseBrandResearchPollInput): UseBrandResearchPollResult {
  const { activeWorkspace, refreshActiveWorkspace } = input;

  /** Mirror the active workspace into a ref so the interval callback always reads fresh state. */
  const activeWorkspaceRef = useRef<Workspace | null>(activeWorkspace);
  activeWorkspaceRef.current = activeWorkspace;

  const pollAttemptsRef = useRef(0);

  /**
   * One-way flag set when the poll budget exhausts so the PostHog event fires
   * exactly once per workspace mount, not once per re-render.
   */
  const [pollExhausted, setPollExhausted] = useState(false);

  const hasSuggestions =
    !!activeWorkspace?.suggested_objectives &&
    activeWorkspace.suggested_objectives.length > 0;

  const researchStatus = activeWorkspace?.research_status ?? 'idle';

  const shouldPoll =
    !pollExhausted &&
    !hasSuggestions &&
    !!activeWorkspace?.website_url &&
    (researchStatus === 'idle' || researchStatus === 'in_progress');

  const showResearchPlaceholder = shouldPoll;

  useEffect(() => {
    if (!shouldPoll) return undefined;
    if (!refreshActiveWorkspace) return undefined;

    pollAttemptsRef.current = 0;
    let cancelled = false;

    const interval = setInterval(() => {
      pollAttemptsRef.current += 1;
      const attempt = pollAttemptsRef.current;

      void refreshActiveWorkspace();

      if (attempt >= BRAND_RESEARCH_POLL_MAX_ATTEMPTS) {
        if (cancelled) return;
        clearInterval(interval);
        const ws = activeWorkspaceRef.current;
        captureException(
          new Error('Brand research suggestion poll exceeded 30s budget'),
          'BrandResearch.suggestionPollTimeout',
          {
            workspaceId: ws?.id ?? null,
            websiteUrl: ws?.website_url ?? null,
          },
        );
        setPollExhausted(true);
      }
    }, BRAND_RESEARCH_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [shouldPoll, refreshActiveWorkspace]);

  /** Reset the exhausted flag on workspace switch so each workspace gets a fresh 30s budget. */
  useEffect(() => {
    setPollExhausted(false);
    pollAttemptsRef.current = 0;
  }, [activeWorkspace?.id]);

  return { showResearchPlaceholder };
}
